import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Where a protected path goes when demo mode is running. The in-app sidebar links point at
// the real paths, so without this a click on "Seller Profile" mid-recording lands on the
// login screen.
const DEMO_EQUIVALENT = {
  '/profile': '/demo/profile',
  '/account': '/demo/account',
  '/app': '/demo',
};

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--bg)',
        fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--text-muted)',
      }}>
        Loading…
      </div>
    );
  }

  // If Supabase is not configured, allow access (backend also skips auth)
  const supabaseConfigured = !!(
    process.env.REACT_APP_SUPABASE_URL &&
    process.env.REACT_APP_SUPABASE_ANON_KEY
  );
  if (!user && supabaseConfigured) {
    // The attribute is set only while DemoProvider is mounted, and DemoProvider does not
    // exist in a built bundle, so this branch is unreachable in production.
    const inDemo = typeof document !== 'undefined' && document.body.dataset.demoMode === 'true';
    const demoPath = inDemo ? DEMO_EQUIVALENT[location.pathname] : null;
    if (demoPath) return <Navigate to={demoPath} replace />;

    return <Navigate to="/login" replace />;
  }

  // Signed in, but the address was never confirmed (SCRUM-49). This is the courtesy half of
  // the bypass guard - it puts the user on the page that can actually fix the situation
  // instead of an app shell whose every request would 403. The enforcing half is the
  // backend's requireAuth, which rejects an unverified token no matter what the browser
  // decides to render.
  //
  // Accounts created before verification existed came out of /api/register already
  // confirmed, so this is transparent to every user who signed up before today.
  if (user && !(user.email_confirmed_at || user.confirmed_at)) {
    return <Navigate to="/verify-email" replace />;
  }

  return children;
}
